import type { Theme as EmotionTheme } from '@emotion/react';
import styled from '@emotion/styled';
import Menu from '@engineering/icons/menu';
import User from '@engineering/icons/user';
import { HeaderIconStyles } from '@silvertours/front-entities';
import { Theme } from '@silvertours/front-shared';

/** The height of the floating header below md (56px, as in the design). */
const floatingMobileHeight = (theme: EmotionTheme) =>
  `calc(${theme.spacing[40]} * 2 + ${theme.spacing[50]})`;

/** Below md the bar is full width, with the sizes of the design. */
const floatingMobileStyles = (theme: EmotionTheme) => {
  const mdBreakpoint = Theme.getBreakpoint('md');
  if (mdBreakpoint === undefined) {
    return '';
  }

  return `
    @media (max-width: ${mdBreakpoint - 1}px) {
      height: ${floatingMobileHeight(theme)};

      > a svg {
        width: auto;
        height: ${theme.spacing[50]};
      }

      #header-action-buttons {
        gap: ${theme.spacing[40]};
      }
    }
  `;
};

const Header = styled('header', {
  shouldForwardProp: prop => prop !== 'isFloating',
})<{ isFloating?: boolean }>`
  height: ${({ theme }) => theme.spacing[70]};
  position: relative;
  z-index: 30;
  display: flex;
  align-items: center;

  ${({ theme }) => theme.mq('lg')} {
    height: ${({ theme }) => theme.spacing[100]};
    z-index: auto;
  }

  ${({ isFloating, theme }) =>
    isFloating &&
    `
    ${floatingMobileStyles(theme)}

    ${theme.mq('md')} {
      height: ${theme.spacing[90]};
      max-width: ${Theme.getBreakpoint('xl')}px;
      margin-inline: auto;
      padding: ${theme.spacing[30]} ${theme.spacing[50]};
      border-radius: ${theme.radius.md};
      background-color: ${theme.colors.neutral.white100};
    }
  `}
`;

/**
 * The area around the floating header bar. It has no background: the page
 * moves its stage up behind it (see `getFloatingHeaderHeight`).
 */
const FloatingFrame = styled.div`
  ${({ theme }) => theme.mq('md')} {
    padding: ${({ theme }) => theme.spacing[50]}
      ${({ theme }) => theme.spacing[60]} 0;
  }
`;

/**
 * The height of the floating header, including the frame around the bar.
 * A section below can use it to move up behind the header.
 */
const getFloatingHeaderHeight = (theme: EmotionTheme) => ({
  base: floatingMobileHeight(theme),
  md: `calc(${theme.spacing[50]} + ${theme.spacing[90]})`,
});

const MenuIcon = styled(Menu)`
  ${HeaderIconStyles};
  /* For accessibility reasons, the menu icon should be slightly bigger. */
  width: ${({ theme }) => theme.spacing[60]};
  height: ${({ theme }) => theme.spacing[60]};
`;

const Separator = styled.hr`
  height: ${({ theme }) => theme.spacing[60]};
  width: 2px;
  flex: none;
  border: 0;
  background-color: #5e6e6a;
  display: block;
  margin-right: ${({ theme }) => theme.spacing[50]};
  align-items: center;
`;

const UserIcon = styled(User)`
  ${HeaderIconStyles}
`;

const IconButtonContainer = styled.div`
  display: flex;
  color: ${({ theme }) => theme.colors.brand.green120};
`;

const ActionArea = styled.div`
  display: flex;
  flex-grow: 1;
  margin-left: ${({ theme }) => theme.spacing[50]};
  justify-content: flex-end;
`;

const DesktopActions = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  width: 100%;
  gap: ${({ theme }) => theme.spacing[50]};
`;

const ActionButtons = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing[50]};
`;

export {
  ActionArea,
  ActionButtons,
  DesktopActions,
  FloatingFrame,
  getFloatingHeaderHeight,
  Header,
  IconButtonContainer,
  MenuIcon,
  Separator,
  UserIcon,
};
